import { AuthenticatedRequest, HTTPMethod, APIVersion, BasicRequest } from "../requests/request";
import { User } from "../models";

export interface Plan {
    id: number
    title: string
    duration: 'monthly',
    price: string
    old_price?: string
    percentage_off?: string;
    price_note?: string
    features?: string[]
    group: string;
}

export interface CouponUsageCalc {
    original_amount: string
    off_amount: string
    final_amount: string
    type: 'percentage_off' | 'dollar_off' | 'promo_credits' | 'regular_credits'
    monthly_activation_count?: number
    value: number
    schedule?: {
        renewal_price: string;
        renewals?: number
        renewal_price_after: string
    }
}

export interface SubscriptionPackage {
    id: number,
    period: 0 | 1 | 3 | 6 | 12,
    amount: string,
    trial_amount?: string,
    trial_period: 0 | 1 | 3 | 6 | 12,
    amount_of_credits?: number
    instructions?: string
    name: string,
    description: string
}

export interface SubscriptionPackageChange {
    mode?: 'upgrade' | 'period' | 'downgrade'
    start_date?: string;
    expire_date?: string;
    package: SubscriptionPackage,
    info?: ChangeSubscription,
    willBeFutureUpdate: boolean
    admin_possible?: boolean
    new_cc_required?: boolean
}

export interface ChangeSubscription extends CouponUsageCalc {
    pro_rata: string
    final_off_amount: string
}

export class API {

    public static cancelSubscription(platform?: 'supreme' | 'stream' | 'create' | 'sync') {
        let apiRequest = new AuthenticatedRequest<any>('/subscription/cancel', HTTPMethod.PUT, APIVersion.v3_0, { platform });
        return apiRequest.send().then((response) => {
            return <any>response.data;
        });
    }

    public static changeSubscriptionPreview(packageId: number, date?: Date, coupon?: string) {
        let apiRequest = new AuthenticatedRequest<SubscriptionPackageChange>('/subscription/change/' + packageId + '/preview', HTTPMethod.PUT, APIVersion.v3_0, undefined, {
            date,
            coupon
        });
        return apiRequest.send().then((response) => {
            return <SubscriptionPackageChange>response.data;
        });
    }

    public static changeSubscription(packageId: number, date: Date, paymentData?: { redirect_base: string } | { name?: string, pan: string, cvv: string, expYear: string, expMonth: string }, coupon?: string) {
        let apiRequest = new AuthenticatedRequest<{ redirect: string } | any>('/subscription/change/' + packageId, HTTPMethod.PUT, APIVersion.v3_0, undefined, {
            date,
            coupon,
            payment_data: paymentData
        });
        return apiRequest.send().then((response) => {
            return <{ redirect: string } | any>response.data;
        });
    }

    public static getAvailablePlans(): Promise<Plan[]> {
        let apiRequest = new AuthenticatedRequest<Plan>('/packages/available', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return response.data.plans;
        });
    }

    public static getPackage(id: number, platform?: 'supreme' | 'stream' | 'create' | 'sync'): Promise<[SubscriptionPackage, boolean]> {
        let apiRequest = new AuthenticatedRequest<{ package: SubscriptionPackage, trial_applicable: boolean }>('/packages/' + id, HTTPMethod.GET, APIVersion.v3_0, { platform });
        return apiRequest.send().then((response) => {
            return [response.data.package, response.data.trial_applicable] as any;
        });
    }

    public static checkCoupon(plan: string, code: string): Promise<{ coupon: { name: string }, info: CouponUsageCalc }> {
        let apiRequest = new AuthenticatedRequest<{ coupon: { name: string }, info: CouponUsageCalc }>('/coupon/' + plan + '/' + encodeURIComponent(code), HTTPMethod.GET, APIVersion.v3_0, undefined);
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static subscribeCreditCard(plan: string, card?: { name?: string, pan: string, cvv: string, expYear: string, expMonth: string }, coupon?: string, from: string = 'record_pool'): Promise<any> {
        let apiRequest = new AuthenticatedRequest<any>('/subscribe/' + plan + '/creditcard', HTTPMethod.POST, APIVersion.v3_0, undefined, {
            ...card,
            coupon,
            from
        });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static subscribePaypal(plan: string, redirect: string, coupon?: string, from: string = 'record_pool'): Promise<{ redirect: string }> {
        let apiRequest = new AuthenticatedRequest<any>('/subscribe/' + plan + '/paypal', HTTPMethod.POST, APIVersion.v3_0, undefined, {
            redirect_base: redirect,
            coupon,
            from
        });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static getAvailablePlansForUser(user: User): Promise<Plan[]> {
        let apiRequest = new AuthenticatedRequest<Plan>('/admin/user/' + user.id + '/availablePackages', HTTPMethod.GET, APIVersion.v2_0);

        return apiRequest.send().then((response) => {
            return response.data.plans;
        });
    }

    public static cancelSubscriptionForUser(platform: 'supreme' | 'stream' | 'create' | 'sync', user: User, now: boolean) {
        let apiRequest = new AuthenticatedRequest<any>('/admin/user/' + user.id + '/subscription/cancel', HTTPMethod.DELETE, APIVersion.v2_0, { now: now ? '1' : '0', platform });
        return apiRequest.send().then((response) => {
            return <any>response.data;
        });
    }

    public static addFreeSubscription(platform: 'supreme' | 'stream' | 'create' | 'sync', userId: string, type: string, expire_date: Date, library?: string): Promise<any> {
        let apiRequest = new AuthenticatedRequest<any>('/admin/user/' + userId + '/freeSubscription', HTTPMethod.POST, APIVersion.v1_0, undefined, {
            expire_date,
            type,
            platform,
            library
        });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static startSubscriptionProcess(platform: 'supreme' | 'stream' | 'create' | 'sync'): Promise<void> {
        let apiRequest = new AuthenticatedRequest<void>('/subscription/start-process', HTTPMethod.POST, APIVersion.v3_0, { platform });

        return apiRequest.send().then((response) => {
            return void 0;
        });
    }
}


// WEBPACK FOOTER //
// ./src/api/src/endpoints/payment.ts